import { useState, useEffect } from 'react';
import { useOutletContext, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Heart, ShoppingCart, Trash2, Loader2, ShoppingBag, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getWishlistIds = () => {
  try { return JSON.parse(localStorage.getItem('dharmshop_wishlist') || '[]'); }
  catch { return []; }
};

const setWishlistIds = (ids) => {
  try { localStorage.setItem('dharmshop_wishlist', JSON.stringify(ids)); } catch (_) { /* noop */ }
};

export default function ShopWishlistPage() {
  const { user, refreshCart } = useOutletContext();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(null);

  useEffect(() => {
    fetchWishlistProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchWishlistProducts = async () => {
    setLoading(true);
    const ids = getWishlistIds();
    if (!ids.length) { setProducts([]); setLoading(false); return; }
    try {
      const fetched = await Promise.all(
        ids.map((id) => axios.get(`${API}/shop/products/${id}`).then((r) => r.data).catch(() => null))
      );
      setProducts(fetched.filter(Boolean));
    } catch {
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const removeFromWishlist = (productId) => {
    const ids = getWishlistIds().filter((id) => id !== productId);
    setWishlistIds(ids);
    setProducts((prev) => prev.filter((p) => p.id !== productId));
    toast.success('Removed from wishlist');
  };

  const moveToCart = async (productId) => {
    if (!user) {
      toast.error('Please login first');
      navigate('/auth');
      return;
    }
    setAdding(productId);
    try {
      const token = localStorage.getItem('dharmbook_token');
      await axios.post(`${API}/shop/cart`,
        { product_id: productId, quantity: 1 },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Moved to cart!');
      refreshCart?.();
      removeFromWishlist(productId);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to move to cart');
    } finally {
      setAdding(null);
    }
  };

  return (
    <div className="shop-page" data-testid="wishlist-page" style={{ paddingBottom: 100 }}>
      <div className="shop-fk-section" style={{ marginTop: 8 }}>
        <h1 className="shop-fk-section-title">My Wishlist</h1>
        <p className="shop-fk-section-subtitle">{products.length} item{products.length !== 1 ? 's' : ''}</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
        </div>
      ) : products.length === 0 ? (
        <div className="shop-fk-section" data-testid="wishlist-empty">
          <div className="text-center py-10">
            <Heart className="w-14 h-14 text-gray-300 mx-auto mb-3" />
            <h3 className="text-base font-bold text-gray-800 mb-1">Your wishlist is empty</h3>
            <p className="text-sm text-gray-500 mb-5">Tap the heart on any product to save it here.</p>
            <Link to="/shop" className="inline-flex items-center gap-1 px-5 py-2.5 bg-[#FF7A00] text-white font-bold rounded-lg text-sm" data-testid="wishlist-shop-now">
              Discover Products <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      ) : (
        <div className="shop-fk-section">
          <div className="divide-y divide-gray-100">
            {products.map((p) => {
              const hasDiscount = p.original_price && p.original_price > p.price;
              const off = hasDiscount ? Math.round((1 - p.price / p.original_price) * 100) : 0;
              return (
                <div key={p.id} className="flex gap-3 py-3" data-testid={`wishlist-item-${p.id}`}>
                  <Link to={`/shop/product/${p.id}`} className="flex-shrink-0">
                    <img
                      src={p.images?.[0] || '/dharmbook-logo.png'}
                      alt={p.name}
                      className="w-20 h-20 rounded-md object-cover bg-gray-50 border border-gray-100"
                    />
                  </Link>

                  <div className="flex-1 min-w-0">
                    <Link to={`/shop/product/${p.id}`}>
                      <h3 className="text-sm font-semibold text-gray-900 line-clamp-2">{p.name}</h3>
                    </Link>
                    <div className="flex items-baseline gap-2 mt-1">
                      <span className="text-base font-bold text-gray-900">₹{p.price}</span>
                      {hasDiscount && <span className="text-xs text-gray-400 line-through">₹{p.original_price}</span>}
                      {hasDiscount && <span className="text-xs text-green-600 font-bold">{off}% off</span>}
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                      <button
                        onClick={() => moveToCart(p.id)}
                        disabled={adding === p.id || p.stock === 0}
                        className="flex-1 inline-flex items-center justify-center gap-1.5 bg-[#FF7A00] text-white text-xs font-bold py-1.5 rounded-md"
                        data-testid={`wishlist-move-${p.id}`}
                      >
                        {adding === p.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <><ShoppingCart className="w-3 h-3" /> Move to Cart</>}
                      </button>
                      <button
                        onClick={() => removeFromWishlist(p.id)}
                        className="px-2 py-1.5 border border-gray-200 rounded-md text-gray-500 hover:text-red-500 hover:border-red-200"
                        data-testid={`wishlist-remove-${p.id}`}
                        aria-label="Remove from wishlist"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {!user && products.length > 0 && (
        <div className="shop-fk-section">
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-xs text-gray-700">
            <ShoppingBag className="w-4 h-4 text-orange-600 inline-block mr-1" />
            <Link to="/auth" className="text-[#FF7A00] font-bold">Login</Link> to sync your wishlist across devices.
          </div>
        </div>
      )}
    </div>
  );
}
